import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const TYPES = [
  { v: "clinical_service", l: "Clinical Service" },
  { v: "existing_client_service", l: "Existing Client Service" },
  { v: "program", l: "Program" },
  { v: "product", l: "Product / Functional Formula" },
];
const STATUSES = ["active", "draft", "archived"];
const EMPTY = {
  type: "clinical_service", title: "", short_description: "", price_display: "",
  cta_label: "Book", square_appointment_url: "", square_payment_url: "",
  internal_route: "", existing_clients_only: false, status: "active", display_order: 0,
};

export default function ClinicOffers() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(null); // null = list view
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clinic-offers/admin");
      setOffers(data.offers || []);
    } catch (e) { setMsg("Failed to load offers"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setMsg("");
    try {
      if (form.id) await api.put(`/clinic-offers/${form.id}`, form);
      else await api.post("/clinic-offers", form);
      setForm(null); await load(); setMsg("Saved.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this offer permanently?")) return;
    try { await api.delete(`/clinic-offers/${id}`); await load(); } catch { setMsg("Delete failed"); }
  };
  const quickStatus = async (o, status) => {
    try { await api.put(`/clinic-offers/${o.id}`, { ...o, status }); await load(); } catch { setMsg("Update failed"); }
  };

  const input = "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-display text-lg text-slate-900">KISSED Clinic OS · Services &amp; Offers</span>
          <Link to="/admin" className="text-xs text-purple-900 hover:underline">← Admin Hub</Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8" data-testid="clinic-offers-admin">
        {msg && <div className="mb-4 text-sm text-purple-900 bg-purple-50 rounded-lg px-3 py-2" data-testid="offers-msg">{msg}</div>}

        {!form ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-display text-2xl text-slate-900">Manage Offerings</h1>
              <button onClick={() => setForm({ ...EMPTY })} className="rounded-full bg-purple-900 text-white text-sm px-4 py-2" data-testid="offers-new">+ New Offer</button>
            </div>
            {loading ? <p className="text-sm text-slate-500">Loading…</p> : (
              <div className="bg-white rounded-xl border border-slate-200 divide-y">
                {offers.map((o) => (
                  <div key={o.id} className="p-4 flex items-center gap-4" data-testid={`offer-row-${o.id}`}>
                    <div className="w-10 text-center text-xs text-slate-400">{o.display_order}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{o.title} {o.existing_clients_only && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 ml-1">Existing Clients</span>}</div>
                      <div className="text-xs text-slate-500 truncate">{TYPES.find(t=>t.v===o.type)?.l} · {o.square_payment_url || o.square_appointment_url || o.internal_route || "no destination"}</div>
                    </div>
                    <select value={o.status} onChange={(e) => quickStatus(o, e.target.value)} className="text-xs border border-slate-300 rounded px-2 py-1" data-testid={`offer-status-${o.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => setForm({ ...o })} className="text-xs text-purple-900 hover:underline" data-testid={`offer-edit-${o.id}`}>Edit</button>
                    <button onClick={() => remove(o.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                  </div>
                ))}
                {offers.length === 0 && <div className="p-6 text-sm text-slate-500">No offers yet.</div>}
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
            <h1 className="font-display text-xl text-slate-900 mb-4">{form.id ? "Edit Offer" : "New Offer"}</h1>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm">Type<select className={input} value={form.type} onChange={(e)=>set("type",e.target.value)} data-testid="form-type">{TYPES.map(t=><option key={t.v} value={t.v}>{t.l}</option>)}</select></label>
              <label className="text-sm">Status<select className={input} value={form.status} onChange={(e)=>set("status",e.target.value)}>{STATUSES.map(s=><option key={s} value={s}>{s}</option>)}</select></label>
              <label className="text-sm sm:col-span-2">Title<input className={input} value={form.title} onChange={(e)=>set("title",e.target.value)} data-testid="form-title" /></label>
              <label className="text-sm sm:col-span-2">Short description<textarea className={input} rows={2} value={form.short_description} onChange={(e)=>set("short_description",e.target.value)} /></label>
              <label className="text-sm">Price display (optional)<input className={input} value={form.price_display} onChange={(e)=>set("price_display",e.target.value)} placeholder="$47 or leave blank" /></label>
              <label className="text-sm">CTA / button text<input className={input} value={form.cta_label} onChange={(e)=>set("cta_label",e.target.value)} data-testid="form-cta" /></label>
              <label className="text-sm sm:col-span-2">Square Appointments URL<input className={input} value={form.square_appointment_url} onChange={(e)=>set("square_appointment_url",e.target.value)} data-testid="form-appt-url" placeholder="https://book.squareup.com/..." /></label>
              <label className="text-sm sm:col-span-2">Square Payment Link URL<input className={input} value={form.square_payment_url} onChange={(e)=>set("square_payment_url",e.target.value)} placeholder="https://square.link/u/..." /></label>
              <label className="text-sm">Internal Clinic route (optional)<input className={input} value={form.internal_route} onChange={(e)=>set("internal_route",e.target.value)} placeholder="/wellness" /></label>
              <label className="text-sm">Display order<input type="number" className={input} value={form.display_order} onChange={(e)=>set("display_order",Number(e.target.value))} /></label>
              <label className="text-sm flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={form.existing_clients_only} onChange={(e)=>set("existing_clients_only",e.target.checked)} /> Existing Clients Only</label>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={save} className="rounded-full bg-purple-900 text-white text-sm px-5 py-2" data-testid="form-save">Save</button>
              <button onClick={() => setForm(null)} className="rounded-full border border-slate-300 text-sm px-5 py-2">Cancel</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
